import type {
  ApiEnvelope,
  MediaAsset,
  PageMeta,
  PublicationResourceType,
  PublishedReference,
  PublishedReferenceConflict,
} from '../types/domain';
import { CONTENT_LANGUAGE_HEADER, getContentLanguage } from '../i18n/content-language';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api/admin/v1').replace(/\/$/, '');
const ACCESS_TOKEN_KEY = 'shmh_admin_access_token';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly details?: unknown,
    public readonly requestId?: string,
  ) {
    super(message);
  }
}

export const tokenStore = {
  get: () => sessionStorage.getItem(ACCESS_TOKEN_KEY),
  set: (token?: string | null) => {
    if (token) sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    else sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  },
};

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  expectedRowVersion?: number;
  idempotencyKey?: string;
  skipRefresh?: boolean;
};

let refreshPromise: Promise<string | null> | null = null;

const buildUrl = (path: string, query?: RequestOptions['query']) => {
  const normalized = path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return normalized;
  const url = new URL(normalized, window.location.origin);
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  });
  return normalized.startsWith('http') ? url.toString() : `${url.pathname}${url.search}`;
};

const refreshAccessToken = async () => {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(async (response) => {
        if (!response.ok) return null;
        const json = await response.json();
        const token = json.data?.accessToken ?? json.accessToken ?? null;
        tokenStore.set(token);
        return token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, expectedRowVersion, idempotencyKey, skipRefresh, ...init } = options;
  const headers = new Headers(init.headers);
  const token = tokenStore.get();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (body !== undefined && !(body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (expectedRowVersion !== undefined) headers.set('If-Match', String(expectedRowVersion));
  if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey);
  headers.set(CONTENT_LANGUAGE_HEADER, getContentLanguage());

  const response = await fetch(buildUrl(path, query), {
    ...init,
    credentials: 'include',
    headers,
    body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
  });

  if (response.status === 401 && !skipRefresh && !path.includes('/auth/')) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return request<T>(path, { ...options, skipRefresh: true });
    tokenStore.set(null);
    window.dispatchEvent(new Event('shmh:unauthorized'));
  }

  const contentType = response.headers.get('content-type') ?? '';
  const json = contentType.includes('json') ? await response.json() : null;
  if (!response.ok) {
    throw new ApiError(
      json?.message ?? `请求失败（${response.status}）`,
      response.status,
      json?.code,
      json?.details,
      json?.requestId ?? response.headers.get('x-request-id') ?? undefined,
    );
  }
  if (response.status === 204) return undefined as T;
  return (json?.data ?? json) as T;
}

export async function requestList<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T[]; meta: PageMeta }> {
  const { body, query, expectedRowVersion, idempotencyKey, skipRefresh, ...init } = options;
  const headers = new Headers(init.headers);
  const token = tokenStore.get();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  headers.set(CONTENT_LANGUAGE_HEADER, getContentLanguage());
  const response = await fetch(buildUrl(path, query), { ...init, credentials: 'include', headers });
  if (response.status === 401 && !skipRefresh) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return requestList<T>(path, { ...options, skipRefresh: true });
  }
  const json = (await response.json()) as ApiEnvelope<T[]> & { items?: T[]; total?: number };
  if (!response.ok) throw new ApiError((json as unknown as { message?: string }).message ?? '请求失败', response.status);
  return {
    data: json.data ?? json.items ?? [],
    meta: json.meta ?? { total: json.total ?? (json.data ?? json.items ?? []).length },
  };
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
};

export const getApiErrorMessage = (error: unknown) => {
  if (error instanceof ApiError) {
    if (error.code === 'SLUG_ALREADY_EXISTS') return 'Slug 已被同类型内容占用，请更换后重试。';
    if (error.code === 'VERSION_CONFLICT') return '内容已被其他成员更新，请刷新后重新编辑。';
    return error.requestId ? `${error.message}（Request ID: ${error.requestId}）` : error.message;
  }

  // Ant Design validateFields() rejects with a plain object instead of Error.
  // Preserve its field-level reasons so callers do not mistake a client-side
  // validation failure (which sends no request) for an unavailable backend.
  if (typeof error === 'object' && error !== null && 'errorFields' in error) {
    const errorFields = (error as {
      errorFields?: Array<{ errors?: unknown[] }>;
      outOfDate?: boolean;
    }).errorFields;
    const reasons = [...new Set(
      (errorFields ?? [])
        .flatMap((field) => field.errors ?? [])
        .filter((reason): reason is string => typeof reason === 'string' && reason.trim().length > 0)
        .map((reason) => reason.trim()),
    )];

    if (reasons.length > 0) return `表单校验未通过：${reasons.join('；')}`;
    if ((error as { outOfDate?: boolean }).outOfDate) return '表单内容已发生变化，请检查后重新保存。';
    return '表单校验未通过，请检查页面中标红的字段。';
  }

  return error instanceof Error ? error.message : '操作失败，请稍后重试。';
};

const publicationResourceTypes = new Set<PublicationResourceType>([
  'SITE',
  'PAGE',
  'PRODUCT',
  'PROJECT',
  'SOLUTION',
  'DOCUMENT',
  'FAQ',
]);

export function getPublishedReferenceConflict(error: unknown): PublishedReferenceConflict | undefined {
  if (!(error instanceof ApiError) || error.code !== 'RESOURCE_STILL_REFERENCED') return undefined;
  const details = error.details && typeof error.details === 'object' && !Array.isArray(error.details)
    ? error.details as Record<string, unknown>
    : {};
  const rawReferences = Array.isArray(details.references) ? details.references : [];
  const references = rawReferences.flatMap((raw): PublishedReference[] => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
    const item = raw as Record<string, unknown>;
    if (
      typeof item.resourceType !== 'string'
      || !publicationResourceTypes.has(item.resourceType as PublicationResourceType)
      || typeof item.resourceId !== 'string'
    ) return [];
    return [{
      resourceType: item.resourceType as PublicationResourceType,
      resourceId: item.resourceId,
      ...(typeof item.snapshotId === 'string' ? { snapshotId: item.snapshotId } : {}),
      ...(typeof item.title === 'string' ? { title: item.title } : {}),
      locations: Array.isArray(item.locations)
        ? item.locations.filter((location): location is string => typeof location === 'string')
        : [],
      ...(typeof item.pageKey === 'string' ? { pageKey: item.pageKey } : {}),
      ...(typeof item.scope === 'string' ? { scope: item.scope } : {}),
    }];
  });
  if (!references.length) return undefined;
  return { message: error.message, requestId: error.requestId, references };
}

export interface UploadSession {
  id?: string;
  sessionId: string;
  mode?: 'DIRECT' | 'MULTIPART';
  uploadUrl?: string;
  method?: 'PUT' | 'POST';
  headers?: Record<string, string>;
  requiredHeaders?: Record<string, string>;
  formFields?: Record<string, string>;
  uploadId?: string;
  asset?: { id: string };
}

type UploadKind = 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'MODEL' | 'ARCHIVE' | 'OTHER';

const inferUpload = (file: File, requestedKind?: Exclude<UploadKind, 'OTHER'>) => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const kind: UploadKind = requestedKind
    ?? (file.type.startsWith('image/') ? 'IMAGE'
      : file.type.startsWith('video/') ? 'VIDEO'
        : file.type === 'application/pdf' ? 'DOCUMENT'
          : extension === 'glb' ? 'MODEL'
            : extension === 'zip' ? 'ARCHIVE' : 'OTHER');
  const mimeType = file.type || ({ glb: 'model/gltf-binary', zip: 'application/zip', pdf: 'application/pdf' }[extension ?? ''] ?? 'application/octet-stream');
  const visibility = kind === 'DOCUMENT' || kind === 'MODEL' || kind === 'ARCHIVE' || kind === 'OTHER' ? 'PRIVATE' : 'PUBLIC';
  return { kind, mimeType, visibility };
};

export async function getMediaAsset(id: string): Promise<MediaAsset> {
  try {
    return await api.get<MediaAsset>(`/media/${id}`);
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 404) throw error;
    const firstPage = await requestList<MediaAsset>('/media', { query: { page: 1, pageSize: 100 } });
    const asset = firstPage.data.find((item) => item.id === id);
    if (!asset) throw error;
    return asset;
  }
}

const waitForMediaReady = async (assetId: string, timeoutMs = 120_000) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const asset = await getMediaAsset(assetId);
    if (asset.status === 'READY') return asset;
    if (asset.status === 'REJECTED' || asset.status === 'FAILED') {
      throw new ApiError('文件安全校验失败', 422, 'MEDIA_REJECTED');
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1200));
  }
  throw new ApiError('文件仍在处理中，请稍后在媒体库查看', 408, 'MEDIA_PROCESSING_TIMEOUT');
};

export async function uploadAsset(file: File, purpose = 'ADMIN_MEDIA', requestedKind?: Exclude<UploadKind, 'OTHER'>) {
  const inferred = inferUpload(file, requestedKind);
  const session = await api.post<UploadSession>('/media/upload-sessions', {
    originalName: file.name,
    mimeType: inferred.mimeType,
    sizeBytes: file.size,
    kind: inferred.kind,
    visibility: inferred.visibility,
    purpose,
  }, { idempotencyKey: crypto.randomUUID() });

  if (session.asset?.id) return session.asset.id;
  let parts: Array<{ partNumber: number; etag: string }> | undefined;
  if (session.mode === 'MULTIPART') {
    const chunkSize = 10 * 1024 * 1024;
    const count = Math.ceil(file.size / chunkSize);
    const partNumbers = Array.from({ length: count }, (_, index) => ({ partNumber: index + 1 }));
    const signed: Array<{ partNumber: number; url: string }> = [];
    for (let offset = 0; offset < partNumbers.length; offset += 100) {
      signed.push(...await api.post<Array<{ partNumber: number; url: string }>>(`/media/upload-sessions/${session.sessionId}/parts/sign`, {
        parts: partNumbers.slice(offset, offset + 100),
      }));
    }
    parts = [];
    for (const part of signed) {
      const start = (part.partNumber - 1) * chunkSize;
      const response = await fetch(part.url, { method: 'PUT', body: file.slice(start, Math.min(start + chunkSize, file.size)) });
      if (!response.ok) throw new ApiError(`第 ${part.partNumber} 分片上传失败`, response.status, 'OSS_MULTIPART_UPLOAD_FAILED');
      const etag = response.headers.get('etag')?.replaceAll('"', '');
      if (!etag) throw new ApiError('OSS 未暴露 ETag，请检查 Bucket CORS ExposeHeader', 500, 'OSS_ETAG_MISSING');
      parts.push({ partNumber: part.partNumber, etag });
    }
  } else {
    if (!session.uploadUrl) throw new ApiError('后端没有返回上传地址', 500, 'UPLOAD_URL_MISSING');
    const uploadResponse = session.method === 'POST' && session.formFields
    ? await (() => {
        const form = new FormData();
        Object.entries(session.formFields ?? {}).forEach(([key, value]) => form.append(key, value));
        const fixedFile = new File([file], file.name, { type: inferred.mimeType });
        form.append('file', fixedFile);
        return fetch(session.uploadUrl!, { method: 'POST', body: form });
      })()
    : await fetch(session.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': inferred.mimeType, ...(session.requiredHeaders ?? session.headers ?? {}) },
        body: file,
      });
    if (!uploadResponse.ok) throw new ApiError('文件直传 OSS 失败', uploadResponse.status, 'OSS_UPLOAD_FAILED');
  }
  const completed = await api.post<{ assetId?: string; asset?: { id: string }; status?: string }>(
    `/media/upload-sessions/${session.sessionId}/complete`,
    { parts },
    { idempotencyKey: crypto.randomUUID() },
  );
  const assetId = completed.assetId ?? completed.asset?.id;
  if (!assetId) throw new ApiError('上传完成但未返回媒体 ID', 500, 'ASSET_ID_MISSING');
  if (completed.status !== 'READY') await waitForMediaReady(assetId);
  return assetId;
}
